import { untranslatedString } from 'models/translated-string';
import type { CsSelectPopoverOption, CsSelectPopoverSubsection } from './cs-select-popover';

/** a section as the menu draws it after search, remembering the full section it was cut from */
export interface CsSelectPopoverShownSection extends CsSelectPopoverSubsection {
  // the unfiltered section. A radio group must update every radio in it, including ones the
  // search is hiding, or a hidden radio could stay selected beside the newly picked one.
  source: CsSelectPopoverSubsection;
}

/**
 * Turn whichever input the menu was given into the one list of sections the rest of the menu reads.
 * A flat option list becomes a single section with no header, which draws exactly as the flat list did.
 * The option objects are kept, not copied, because rows record their selection on them.
 */
export function toSelectPopoverSections(
  options: CsSelectPopoverOption[] | undefined,
  subsections: CsSelectPopoverSubsection[] | undefined,
): CsSelectPopoverSubsection[] {
  if (subsections) {
    return subsections;
  }
  return [{ header: untranslatedString(''), options: options ?? [] }];
}

/**
 * The sections to draw for a search query: in each section, the options whose name contains the
 * query (ignoring case), in their original order. A section with no match left is dropped, so its
 * header does not hang over nothing. An empty query shows everything.
 */
export function filterSelectPopoverSections(
  sections: CsSelectPopoverSubsection[],
  query: string,
): CsSelectPopoverShownSection[] {
  if (!query) {
    return sections.map(section => ({ ...section, source: section }));
  }
  const lowerQuery = query.toLowerCase();
  return sections
    .map(section => ({
      ...section,
      options: section.options.filter(o => o.name.toLowerCase().includes(lowerQuery)),
      source: section,
    }))
    .filter(section => section.options.length > 0);
}
